import { useState } from "react";
import { Form, Formik } from "formik";
import { object, string } from "yup";
import Callout from "../UI/Callout";
import Text from "../UI/Text";
import Title from "../UI/Title";
import ModalDialog from "../UI/ModalDialog";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select.jsx";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import {
  EMAIL_INVALID_MESSAGE,
  EMAIL_REGEX,
} from "../../utils/constants/validation";
import styles from "./ConnectPennylaneModal.module.css";

type Mode = "new" | "existing";

interface ConnectFormValues {
  email: string;
  mode: Mode;
}

interface ConnectPennylaneModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail: string;
  /** Creates the Pennylane user and company through Qalimo's provisioning (mode "new"). */
  onConnect: (data: { email: string }) => Promise<unknown>;
  /** Redirects to Pennylane's consent screen (mode "existing"); never resolves on success. */
  onAuthorize: () => Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const MODE_OPTIONS: { value: Mode; label: string }[] = [
  { value: "new", label: "Je n'ai pas encore de compte Pennylane" },
  { value: "existing", label: "J'ai déjà un compte Pennylane" },
];

export default function ConnectPennylaneModal({
  open,
  onOpenChange,
  defaultEmail,
  onConnect,
  onAuthorize,
  onSuccess,
}: ConnectPennylaneModalProps) {
  const [error, setError] = useState("");

  return (
    <Formik<ConnectFormValues>
      initialValues={{ email: defaultEmail, mode: "new" }}
      enableReinitialize
      validationSchema={object({
        email: string().when("mode", {
          is: "new",
          then: (schema) =>
            schema
              .matches(EMAIL_REGEX, EMAIL_INVALID_MESSAGE)
              .required("L'adresse e-mail est obligatoire"),
        }),
      })}
      onSubmit={async (values) => {
        setError("");
        try {
          if (values.mode === "existing") {
            await onAuthorize();
            return;
          }
          await onConnect({ email: values.email });
          await onSuccess();
        } catch (e) {
          setError(handleAPIError(e));
        }
      }}
    >
      {({ resetForm, values }) => (
        <ModalDialog
          size="md"
          open={open}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              resetForm();
              setError("");
            }
            onOpenChange(nextOpen);
          }}
          header={<Title variant="mega">Connecter Pennylane</Title>}
        >
          <Form className={styles.form}>
            <ReactSelect
              label="Avez-vous déjà un compte Pennylane ?"
              name="mode"
              options={MODE_OPTIONS}
              isClearable={false}
              isSearchable={false}
            />
            {values.mode === "existing" ? (
              <Text>
                Vous allez être redirigé vers Pennylane pour vous identifier et
                autoriser Qalimo sur la société de ce profil. Si votre société a
                été créée par votre expert-comptable, demandez-lui d&apos;abord
                de vous y inviter.
              </Text>
            ) : (
              <>
                <TextInput
                  type="email"
                  name="email"
                  label="Adresse email du compte Pennylane"
                  placeholder="email@exemple.com"
                  marginForm=""
                />
                <Text variant="deci">
                  Pennylane vous enverra un email pour activer votre compte.
                </Text>
              </>
            )}
            <Callout variant="danger">{error}</Callout>
            <div className={styles.actions}>
              <FormikSubmitButton>Continuer</FormikSubmitButton>
            </div>
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}
